import type { DesignSystem } from '@cogo/kg-repository'
import { getStudioAccessToken } from '@/services/auth-provider'
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaders,
} from '@/services/cogo-platform-config'
import {
  resolvePreview,
  type ResolvedUiBlockPreview,
  type UiBlockRef,
} from './ui-blocks/ui-block-consumer'

type DesignSystemConsumerErrorCode =
  | 'authentication-required'
  | 'request-timeout'
  | 'request-failed'
  | 'invalid-response'

const DESIGN_SYSTEM_INVENTORY_TIMEOUT_MS = 8_000

export class DesignSystemConsumerError extends Error {
  constructor(
    readonly code: DesignSystemConsumerErrorCode,
    message: string,
    readonly status: number | null = null,
  ) {
    super(message)
    this.name = 'DesignSystemConsumerError'
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function stringMap(value: unknown): Record<string, string> | undefined {
  const source = record(value)
  if (!source) return undefined
  const entries = Object.entries(source).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  )
  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

function toDesignSystem(value: unknown, index: number): DesignSystem {
  const source = record(value)
  const id = typeof source?.id === 'string' ? source.id.trim() : ''
  const name = typeof source?.name === 'string' ? source.name.trim() : ''
  if (!id || !name) {
    throw new DesignSystemConsumerError(
      'invalid-response',
      `Design system inventory row ${index + 1} is missing its id or name.`,
    )
  }
  const axes = stringMap(source?.tokens)
  return {
    id,
    iri: `https://cogo.xyz/ns/v1#DesignSystem-${id}`,
    name,
    ...(axes ? { axes } : {}),
  }
}

/**
 * Reads the authenticated workspace inventory through Studio's Cogo identity
 * and same-origin platform proxy. DGX never needs a Supabase browser client,
 * mock, URL, or session for this read.
 */
export async function readWorkspaceDesignSystems(
  signal?: AbortSignal,
): Promise<readonly DesignSystem[]> {
  const accessToken = await getStudioAccessToken()
  if (!accessToken) {
    throw new DesignSystemConsumerError(
      'authentication-required',
      'The authenticated Cogo session is unavailable.',
    )
  }

  const requestController = new AbortController()
  let deadlineReached = false
  const forwardAbort = () => requestController.abort(signal?.reason)
  if (signal?.aborted) forwardAbort()
  else signal?.addEventListener('abort', forwardAbort, { once: true })
  const deadline = setTimeout(() => {
    deadlineReached = true
    requestController.abort()
  }, DESIGN_SYSTEM_INVENTORY_TIMEOUT_MS)

  try {
    let response: Response
    try {
      response = await fetch(cogoPlatformApiUrl('/api/design-systems'), {
        method: 'GET',
        headers: withCogoDevProxyHeaders({
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        }),
        signal: requestController.signal,
      })
    } catch (error) {
      if (deadlineReached) {
        throw new DesignSystemConsumerError(
          'request-timeout',
          'The Cogo platform design system inventory request timed out.',
        )
      }
      if (error instanceof Error && error.name === 'AbortError') throw error
      throw new DesignSystemConsumerError(
        'request-failed',
        error instanceof Error ? error.message : 'The Cogo platform request failed.',
      )
    }

    if (!response.ok) {
      throw new DesignSystemConsumerError(
        'request-failed',
        `The Cogo platform returned HTTP ${response.status}.`,
        response.status,
      )
    }

    let payload: unknown
    try {
      payload = await response.json()
    } catch {
      throw new DesignSystemConsumerError(
        'invalid-response',
        'The Cogo platform returned an unreadable design system inventory.',
        response.status,
      )
    }
    if (!Array.isArray(payload)) {
      throw new DesignSystemConsumerError(
        'invalid-response',
        'The Cogo platform returned an invalid design system inventory.',
        response.status,
      )
    }
    return payload.map(toDesignSystem)
  } finally {
    clearTimeout(deadline)
    signal?.removeEventListener('abort', forwardAbort)
  }
}

/**
 * Component preview reads stay inside the committed, catalog-admitted Cogo
 * UI Block projection. This is deliberately read-only and never falls back
 * to a Supabase MCP client or fabricates an unadmitted catalog row.
 */
export function readAdmittedUiBlockPreview(
  ref: UiBlockRef,
): Promise<ResolvedUiBlockPreview> {
  return resolvePreview(ref)
}
